import mixpanel from 'mixpanel-browser'
import React, { useEffect, useState } from 'react'
import {
  setGlobalMixpanelInstance,
  trackMixpanelEvent,
  identifyMixpanelUser,
  setMixpanelUserProperties,
  incrementMixpanelUserProperties,
  aliasMixpanelUser,
  resetMixpanel,
} from './mixpanel-global'
import { MixpanelContext, MixpanelProviderProps, MixpanelContextType } from './types'
import { SESSION_SAMPLED_KEY } from './constant'

/**
 * `MixpanelProvider` is a React functional component that initializes and provides access
 * to the Mixpanel analytics SDK via a React Context.
 *
 * Props:
 * @param token (string) - The Mixpanel project token. This is required for initializing Mixpanel.
 * @param children (ReactNode) - The child components to be wrapped by the Mixpanel provider.
 * @param tracesSampleRate (number) - Optional sampling rate for traces,
 * used to control the volume of data sent to Mixpanel.
 * @param initialDistinctId (string) - An optional initial distinct ID to identify the user upon initialization.
 * @param config (Config) - Optional Mixpanel configuration options.
 *
 * Returns:
 * The children components wrapped within the Mixpanel context provider.
 *
 * The function:
 * - It handles the initialization of Mixpanel with the provided token and configuration,
 * and optionally identifies a user with an initial distinct ID.
 * - The provider also implements a session sampling logic based on a value stored in
 * local storage, affecting whether tracking events are actually sent to Mixpanel.
 * - It exposes Mixpanel's core functionalities (`track`, `identify`, `alias`, `people.set`, `reset`)
 * and the initialization/identification status (`isInitialized`, `hasIdentifiedUser`, `isSessionSampled`)
 * through a context, intended to be consumed by the `useMixpanel` hook.
 */
export const MixpanelProvider: React.FC<MixpanelProviderProps> = ({
  token,
  children,
  tracesSampleRate,
  initialDistinctId,
  config,
}) => {
  // These states are primarily for the `useMixpanel` hook consumers.
  // They will reflect the global status that `mixpanel-global.ts` manages.
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasIdentifiedUser, setHasIdentifiedUser] = useState(false)
  const [isSessionSampled, setIsSessionSampled] = useState(false) // This will be read from localStorage

  useEffect(() => {
    // Read and set the session sampling status from localStorage
    const sessionSampledStatus = localStorage.getItem(SESSION_SAMPLED_KEY)
    setIsSessionSampled(sessionSampledStatus === 'true')

    if (token && !isInitialized) {
      mixpanel.init(token, { ...config })
      setIsInitialized(true)
      // Pass the initialized mixpanel instance and configuration to the global module
      setGlobalMixpanelInstance(mixpanel, tracesSampleRate)
    } else if (!token) {
      // If token becomes null/undefined after being set, clear global instance and states
      setGlobalMixpanelInstance(null, undefined) // Clear global instance and sampling rate
      setIsInitialized(false)
      setHasIdentifiedUser(false)
      setIsSessionSampled(false) // Reset session sampled status
      console.warn('MixpanelProvider: No token provided. Mixpanel is NOT initialized.')
    }
  }, [token, config, isInitialized, tracesSampleRate])

  useEffect(() => {
    // Handle initial identification via provider. This can trigger the global identify.
    // This effect ensures that the initial distinct ID is set once after initialization.
    if (isInitialized && initialDistinctId && !hasIdentifiedUser) {
      // Use the global identify function, which will handle the queuing if not ready
      identifyMixpanelUser(initialDistinctId)
      setHasIdentifiedUser(true) // Update local state for `useMixpanel` consumers
    }
  }, [isInitialized, initialDistinctId, hasIdentifiedUser])

  // Periodically update isSessionSampled state for hook consumers
  useEffect(() => {
    const handleStorageChange = () => {
      const currentStatus = localStorage.getItem(SESSION_SAMPLED_KEY)
      setIsSessionSampled(currentStatus === 'true')
    }
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // Memoize the Mixpanel API object to prevent unnecessary re-renders for consumers of the context
  // These functions now call the global functions to centralize logic and ensure sampling is applied
  const mixpanelApi: MixpanelContextType = React.useMemo(() => {
    return {
      /**
       * The track method is used to send events to Mixpanel.
       * It takes an event name and an optional properties object.
       */
      track: (eventName, properties) => {
        trackMixpanelEvent(eventName, properties)
        // After tracking, re-read status to ensure local state reflects global decision
        const currentSessionSampledStatus = localStorage.getItem(SESSION_SAMPLED_KEY)
        setIsSessionSampled(currentSessionSampledStatus === 'true')
      },
      /**
       * Calling identify(<user_id>) sets the user_id value passed to the identify function
       * as the $user_id event property for the user’s events moving forward.
       */
      identify: (distinctId, properties) => {
        identifyMixpanelUser(distinctId, properties)
        setHasIdentifiedUser(true) // Update local state for hook consumers
      },
      /**
       * A new distinct_id to be merged with the original distinct_id. Each alias can only map to one distinct_id.
       */
      alias: (alias, originalId) => {
        if (typeof alias === 'string' && typeof originalId === 'string') {
          aliasMixpanelUser(alias, originalId)
        } else {
          console.warn('Mixpanel alias and originalId must be defined strings.')
        }
      },
      /**
       * The people.set method allows you to set properties on a user profile.
       * This is useful for setting user properties that are not event-specific.
       */
      people: {
        set: (properties) => {
          setMixpanelUserProperties(properties)
        },
        increment: (properties) => {
          incrementMixpanelUserProperties(properties)
        },
      },
      /**
       * The reset method clears the current user’s identity and resets the Mixpanel instance.
       * This is useful for logging out a user or resetting the Mixpanel state.
       */
      reset: () => {
        resetMixpanel()
        setHasIdentifiedUser(false) // Update local state
        setIsSessionSampled(false) // Update local state
      },
      // Expose current states for hook consumers
      isInitialized,
      hasIdentifiedUser,
      isSessionSampled,
    }
  }, [isInitialized, hasIdentifiedUser, isSessionSampled])

  return <MixpanelContext.Provider value={mixpanelApi}>{children}</MixpanelContext.Provider>
}
